import { FC } from 'react';
import {
  Box,
  Paper,
  Typography,
  CircularProgress,
  Alert,
} from '@mui/material';
import FolderSharedIcon from '@mui/icons-material/FolderShared';
import { skipToken } from '@reduxjs/toolkit/query';
import { BRAND_DARKER } from './healthWalletConstants';
import {
  useGetUserDocumentsQuery,
  useGetSSEEventsQuery,
} from './healthWalletSlice';
import { UploadDocumentButton } from './UploadDocumentButton';
import { UserDocumentRow } from './UserDocumentRow';

// Shared SSE arg with the records-screen sync card (one EventSource per profile — the map
// listener keys subscribers by profileId, so we must reuse the same key, not open a second).
const SSE_KEY = 0;

/**
 * "My Documents" panel (USER_DOCUMENTS_UI_PLAN.md §5). NO LONGER an accordion — it's now the content of
 * the "My Documents" pill, so the collapse is redundant; we render a flat header (title + upload button,
 * what the accordion summary held) with the list always visible. Data loads eagerly via
 * useGetUserDocumentsQuery (shares the RTK cache key with the pill-count query in HealthWalletRecords, so
 * no extra fetch).
 *
 * Refresh on terminal/delete is now CENTRAL (no component-local SSE effect): getUserDocuments is tagged
 * 'Patient', so the slice's central SSE handler — which fires `invalidateTags(['Patient','Onboarding'])`
 * on `workflow-completed` and on `data-refresh` for EVERY client/map — refetches this list (row →
 * processing✓, pill count) exactly like the summary, identical to how IAS health-records refresh
 * completes. We still subscribe to the shared SSE stream WHILE a doc is processing — not to read events,
 * but to keep that long-lived EventSource (and thus the central handler) alive so the terminal is
 * delivered/processed on the originating client. The old baseline/handledRef event-walk was racy against
 * the central events-array reset (the "user-doc spinner SSE-miss" debt) and is gone.
 */
export const UserDocumentsSection: FC = () => {
  const { data, isFetching, error, refetch } = useGetUserDocumentsQuery();

  const documents = data?.documents ?? [];
  const hasProcessing = documents.some((d) => d.status === 'pending' || d.status === 'processing');

  // Keep the shared SSE connection (and the slice's central terminal handler) alive while a doc is
  // processing. We don't consume the events here — the central handler does, and the 'Patient' tag
  // drives this list's refetch. skipToken → no subscription once nothing is processing.
  useGetSSEEventsQuery(hasProcessing ? SSE_KEY : skipToken);

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 3,
        border: '1px solid #d7d3f0',
        boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
        bgcolor: 'white',
        p: 2,
      }}
    >
      {/* Header (was the accordion summary): title + always-accessible upload button. */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
        <FolderSharedIcon sx={{ color: BRAND_DARKER }} />
        <Typography variant="subtitle1" fontWeight={700} sx={{ color: '#1c1c1e', flex: 1 }}>
          My Documents
          {data && documents.length > 0 && (
            <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
              ({documents.length})
            </Typography>
          )}
        </Typography>
        <UploadDocumentButton onUploaded={refetch} />
      </Box>

      {/* Body (always visible — no collapse). */}
      {isFetching && documents.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={24} sx={{ color: BRAND_DARKER }} />
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ borderRadius: 2 }}>
          Could not load your documents. Please try again.
        </Alert>
      ) : documents.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No documents yet — use the Upload button above to add an image, PDF, or note.
        </Typography>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          {documents.map((doc, i) => (
            <UserDocumentRow key={doc.id} doc={doc} onChanged={refetch} defaultExpanded={i === 0} />
          ))}
        </Box>
      )}
    </Paper>
  );
};
